import create from "zustand";
import {
  Announcement,
  AnnouncementId,
  ANNOUNCEMENT_DEFAULT_TTL_MS,
  FreshAnnouncement,
} from "../../models/Announcement";
import { OwnerId } from "../../models/User";
import { AnnouncementService } from "../../services/AnnouncementService";

export type AnnouncementsState = {
  all: Announcement[];
  dismissedIds: Set<AnnouncementId>;
  /** Announcements that are unexpired AND not dismissed by the current
   *  user. Computed on demand from `all` + `dismissedIds`. */
  active: () => Announcement[];
  observe: (ownerId: OwnerId | undefined) => () => void;
  create: (
    fromOwnerId: OwnerId,
    fromAlias: string,
    title: string,
    body: string,
    options?: { dismissible?: boolean; ttlMs?: number },
  ) => Promise<AnnouncementId>;
  update: (
    id: AnnouncementId,
    patch: Partial<FreshAnnouncement>,
  ) => Promise<void>;
  remove: (id: AnnouncementId) => Promise<void>;
  dismiss: (ownerId: OwnerId, id: AnnouncementId) => Promise<void>;
};

export const useAnnouncements = create<AnnouncementsState>((set, get) => ({
  all: [],
  dismissedIds: new Set(),

  active: () => {
    const now = Date.now();
    const { all, dismissedIds } = get();
    return all.filter(
      (a) => a.expiresAt > now && !dismissedIds.has(a.id),
    );
  },

  observe: (ownerId) => {
    const offAll = AnnouncementService.observeAll((all) => {
      set({ all });
    });
    let offDismissals = () => {};
    if (ownerId) {
      offDismissals = AnnouncementService.observeDismissals(
        ownerId,
        (dismissedIds) => set({ dismissedIds }),
      );
    }
    return () => {
      offAll();
      offDismissals();
    };
  },

  create: (fromOwnerId, fromAlias, title, body, options) => {
    const createdAt = Date.now();
    const fresh: FreshAnnouncement = {
      title,
      body,
      fromOwnerId,
      fromAlias,
      createdAt,
      expiresAt: createdAt + (options?.ttlMs ?? ANNOUNCEMENT_DEFAULT_TTL_MS),
      dismissible: options?.dismissible ?? true,
    };
    return AnnouncementService.create(fresh);
  },

  update: (id, patch) => AnnouncementService.update(id, patch),

  remove: (id) => AnnouncementService.remove(id),

  dismiss: (ownerId, id) => AnnouncementService.dismiss(ownerId, id),
}));
